// src/pages/patrimoines/ListePatrimoines.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Spinner, Modal, Alert, Button, Badge, Table, Pagination, Form, InputGroup } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { ROLES } from '../../data/Menu';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';

// ===== STYLES =====
const styles = `
  .dashboard-styled {
    background: #f4f6f9;
    min-height: 100vh;
  }

  /* En-tête moderne */
  .header-modern {
    background: white;
    padding: 1.2rem 1.8rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    border-left: 4px solid ${COLORS.secondary};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
    background: ${COLORS.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: white;
    font-size: 1.4rem;
  }

  .gradient-text {
    color: #1e293b;
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0;
  }

  .badge-modern {
    background: #f1f4f9;
    color: #475569;
    padding: 0.3rem 0.8rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .badge-modern i {
    color: ${COLORS.secondary};
  }

  .btn-modern-outline {
    border: 1px solid #d1d5db;
    background: white;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #4b5563;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-modern-outline:hover {
    border-color: ${COLORS.secondary};
    color: ${COLORS.secondary};
    background: #fafafa;
  }

  .btn-modern-primary {
    background: ${COLORS.primary};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-primary:hover {
    background: ${COLORS.primaryDark};
    box-shadow: 0 4px 10px rgba(41,90,167,0.3);
  }

  .btn-modern-success {
    background: ${COLORS.success};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-success:hover {
    background: #059669;
    box-shadow: 0 4px 10px rgba(16,185,129,0.3);
  }

  /* KPI Cards */
  .kpi-card {
    background: white;
    border-radius: 4px;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    transition: all 0.2s ease;
    height: 100%;
    border-left: 3px solid;
  }
  .kpi-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.06);
  }
  .kpi-primary { border-left-color: ${COLORS.primary}; }
  .kpi-success { border-left-color: ${COLORS.success}; }
  .kpi-purple { border-left-color: #8b5cf6; }
  .kpi-warning { border-left-color: ${COLORS.warning}; }
  .kpi-danger { border-left-color: ${COLORS.danger}; }

  .kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
  }
  .kpi-primary .kpi-icon { background: ${COLORS.primary}15; color: ${COLORS.primary}; }
  .kpi-success .kpi-icon { background: ${COLORS.success}15; color: ${COLORS.success}; }
  .kpi-purple .kpi-icon { background: #8b5cf615; color: #8b5cf6; }
  .kpi-warning .kpi-icon { background: ${COLORS.warning}15; color: ${COLORS.warning}; }
  .kpi-danger .kpi-icon { background: ${COLORS.danger}15; color: ${COLORS.danger}; }

  .kpi-content { flex: 1; }
  .kpi-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    color: #6c757d;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
  .kpi-value {
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0.1rem 0 0 0;
    color: #1e293b;
  }

  /* Filter card */
  .filter-card {
    border: none;
    border-radius: 4px;
    background: ${COLORS.primary};
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  }
  .filter-card .form-control,
  .filter-card .form-select {
    border-radius: 4px;
    border: none;
    padding: 0.35rem 0.7rem;
    font-size: 0.8rem;
    background: rgba(255,255,255,0.95);
  }
  .filter-card .form-control:focus,
  .filter-card .form-select:focus {
    box-shadow: 0 0 0 3px rgba(255,255,255,0.2);
  }

  /* Table */
  .table-card {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    overflow: hidden;
  }
  .table-header-secondary {
    background: ${COLORS.secondary};
    color: white;
    padding: 0.6rem 1rem;
  }
  .table-modern thead th {
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }
  .table-modern tbody tr {
    transition: all 0.2s ease;
  }
  .table-modern tbody tr:hover {
    background: #f8fafc;
  }

  .reference-badge {
    background: #f1f4f9;
    padding: 0.2rem 0.6rem;
    border-radius: 4px;
    font-size: 0.7rem;
    font-family: monospace;
    color: #334155;
  }

  .action-btn {
    width: 28px;
    height: 28px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    transition: all 0.2s ease;
    font-size: 0.9rem;
  }
  .action-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(0,0,0,0.08);
  }
  .action-btn-edit { background: ${COLORS.secondary}15; color: ${COLORS.secondary}; }
  .action-btn-edit:hover { background: ${COLORS.secondary}; color: white; }
  .action-btn-delete { background: ${COLORS.danger}15; color: ${COLORS.danger}; }
  .action-btn-delete:hover { background: ${COLORS.danger}; color: white; }

  /* Pagination */
  .pagination-modern .page-link {
    border: none;
    margin: 0 2px;
    border-radius: 4px;
    color: #64748b;
    font-size: 0.8rem;
    padding: 0.3rem 0.7rem;
    transition: all 0.2s ease;
  }
  .pagination-modern .page-link:hover {
    background: ${COLORS.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(41,90,167,0.2);
  }
  .pagination-modern .page-item.active .page-link {
    background: ${COLORS.primary};
    color: white;
    box-shadow: 0 4px 8px rgba(41,90,167,0.3);
  }

  /* Loading */
  .loading-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
  }
  .loading-ring-wrapper {
    position: relative;
    width: 70px;
    height: 70px;
  }
  .loading-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 3px solid #e9edf2;
    border-top-color: ${COLORS.primary};
    animation: spin 0.8s linear infinite;
  }
  .loading-ring-inner {
    position: absolute;
    top: 8px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    border-radius: 50%;
    border: 2px solid #e9edf2;
    border-bottom-color: ${COLORS.secondary};
    animation: spin 0.6s linear infinite reverse;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .fade-in {
    animation: fadeIn 0.3s ease-out;
  }

  /* Modal */
  .delete-modal .modal-content {
    border: none;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  }
  .delete-modal .modal-header {
    border-bottom: none;
    padding: 1.2rem 1.5rem 0.5rem;
  }
  .delete-modal .modal-footer {
    border-top: none;
    padding: 0.5rem 1.5rem 1.2rem;
  }

  .delete-icon-circle {
    width: 56px;
    height: 56px;
    border-radius: 4px;
    background: ${COLORS.danger}15;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.8rem;
    color: ${COLORS.danger};
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    .header-modern {
      flex-direction: column;
      align-items: stretch;
      gap: 0.8rem;
      padding: 1rem;
    }
    .header-left {
      flex-wrap: wrap;
    }
  }
`;

// ===== UTILITAIRES =====
const truncateText = (text, maxLength = 25) => {
  if (!text) return '-';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('fr-FR', options);
};

const formatDateSimple = (dateString) => {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('fr-FR', options);
};

const formatQuantite = (quantite) => {
  return new Intl.NumberFormat('fr-FR').format(quantite);
};

export default function ListePatrimoines() {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [templeFilter, setTempleFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [temples, setTemples] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [patrimoineToDelete, setPatrimoineToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    totalQuantite: 0
  });

  // Permissions
  const [permissions, setPermissions] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
    canExport: false,
    canFilter: false
  });

  const hasPermission = (action) => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      const user = JSON.parse(userStr);
      const userRole = user.role || user.type_utilisateur;
      if (userRole === ROLES.SUPER_ADMIN) return true;

      const permissionsMap = {
        'view_patrimoines': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DIACRE, ROLES.DIACONESSE, ROLES.DP, ROLES.ACCUEIL],
        'add_patrimoine': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DIACRE, ROLES.DIACONESSE],
        'edit_patrimoine': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN],
        'delete_patrimoine': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR],
        'export_patrimoines': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DIACRE, ROLES.DIACONESSE],
        'filter_patrimoines': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DIACRE, ROLES.DIACONESSE, ROLES.DP, ROLES.ACCUEIL]
      };
      return permissionsMap[action]?.includes(userRole) || false;
    } catch (e) { return false; }
  };

  useEffect(() => {
    setPermissions({
      canView: hasPermission('view_patrimoines'),
      canAdd: hasPermission('add_patrimoine'),
      canEdit: hasPermission('edit_patrimoine'),
      canDelete: hasPermission('delete_patrimoine'),
      canExport: hasPermission('export_patrimoines'),
      canFilter: hasPermission('filter_patrimoines')
    });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('reload')) {
      setRefreshTrigger(prev => prev + 1);
      navigate('/patrimoines/liste', { replace: true });
    }
  }, [location, navigate]);

  const fetchTemples = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const response = await fetch(`https://josephsbusiness.com/api/eglises.php?user_id=${userId}&parent=filiale&limit=1000`, {
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      if (result.success) setTemples(result.eglises || []);
    } catch (err) { console.error(err); }
  };

  const fetchPatrimoines = useCallback(async () => {
    try {
      setLoading(true);
      const userStr = localStorage.getItem('user');
      if (!userStr) { navigate('/pages/signin2'); return; }
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const timestamp = Date.now();
      let url = `https://josephsbusiness.com/api/patrimoines.php?user_id=${userId}&page=${currentPage}&limit=${itemsPerPage}&_=${timestamp}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (templeFilter) url += `&temple=${encodeURIComponent(templeFilter)}`;

      const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();

      if (result.success) {
        setData(result);
        setStats({
          total: result.stats?.total || 0,
          totalQuantite: result.stats?.total_quantite || 0
        });
      } else {
        setError(result.message || 'Erreur de chargement');
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, templeFilter, itemsPerPage, navigate]);

  useEffect(() => { fetchTemples(); }, []);
  useEffect(() => { fetchPatrimoines(); }, [fetchPatrimoines, refreshTrigger]);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setRefreshTrigger(prev => prev + 1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setRefreshTrigger(prev => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClick = (patrimoine) => {
    setPatrimoineToDelete(patrimoine);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!patrimoineToDelete) return;
    setDeleting(true);
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const response = await fetch(`https://josephsbusiness.com/api/patrimoines.php?user_id=${userId}&id=${patrimoineToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      if (result.success) {
        setShowDeleteModal(false);
        setPatrimoineToDelete(null);
        setRefreshTrigger(prev => prev + 1);
        setSuccessMessage('Patrimoine supprimé avec succès');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      } else {
        alert(result.message || 'Erreur');
      }
    } catch (err) { alert('Erreur: ' + err.message); } finally { setDeleting(false); }
  };

  const getTempleName = (matricule) => {
    const temple = temples.find(t => t.matricule === matricule);
    return temple ? temple.nom : matricule;
  };

  const exportToExcel = () => {
    if (!data?.patrimoines) return;
    const fileName = `patrimoines_${new Date().toISOString().split('T')[0]}.xlsx`;
    const exportData = data.patrimoines.map(item => ({
      'Matricule': item.matricule,
      'Intitulé': item.intitule,
      'Quantité': item.quantite,
      'Date': formatDateSimple(item.date_at),
      'Description': item.description || '-',
      'Temple': getTempleName(item.matri_eglise)
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.sheet_add_aoa(ws, [
      ['LISTE DES PATRIMOINES'],
      [`Généré le ${new Date().toLocaleDateString('fr-FR')}`],
      [`Total items: ${stats.total}`],
      [`Quantité totale: ${stats.totalQuantite}`],
      []
    ], { origin: 'A1' });
    XLSX.utils.book_append_sheet(wb, ws, "Patrimoines");
    XLSX.writeFile(wb, fileName);
  };

  const printPDF = () => {
    const printWindow = window.open('', '_blank');
    const patrimoines = data?.patrimoines || [];
    const html = `
      <!DOCTYPE html>
      <html><head><title>Patrimoines</title><meta charset="UTF-8">
      <style>
        body { font-family: Arial; margin: 30px; }
        .header { text-align: center; border-bottom: 2px solid #295AA7; padding-bottom: 20px; }
        .summary { background: #f8fafc; padding: 15px; border-left: 4px solid #8b5cf6; margin: 20px 0; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th { background: #295AA7; color: white; padding: 8px; text-align: left; }
        td { padding: 6px 8px; border: 1px solid #ddd; }
        tr:nth-child(even) { background: #f8fafc; }
      </style>
      </head>
      <body>
        <div class="header"><h1>Liste des patrimoines</h1><p>Généré le ${new Date().toLocaleDateString('fr-FR')}</p></div>
        <div class="summary">
          <p><strong>Total items:</strong> ${stats.total}</p>
          <p><strong>Quantité totale:</strong> ${stats.totalQuantite}</p>
        </div>
        <table>
          <thead><tr><th>Matricule</th><th>Intitulé</th><th>Quantité</th><th>Date</th><th>Description</th><th>Temple</th></tr></thead>
          <tbody>
            ${patrimoines.map(p => `
              <tr>
                <td>${truncateText(p.matricule, 10)}</td>
                <td>${truncateText(p.intitule, 20)}</td>
                <td>${p.quantite}</td>
                <td>${formatDateSimple(p.date_at)}</td>
                <td>${p.description ? truncateText(p.description, 20) : '-'}</td>
                <td>${truncateText(getTempleName(p.matri_eglise), 15)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

   if (loading) {
     return (
         <>
             <Header />
             <div className="main main-app p-3 p-lg-4 dashboard-styled">
                 <div className="loading-wrapper">
                     <div className="text-center">
                         {/* Logo ou icône animé */}
                         {/* <div className="loading-icon-wrapper mb-4">
                             <div className="loading-icon-pulse">
                                 <i className="ri-building-line" style={{ fontSize: '3.5rem', color: COLORS.primary }}></i>
                             </div>
                         </div> */}
 
                         {/* Anneau de chargement amélioré */}
                         <div className="loading-ring-wrapper mb-4">
                             <div className="loading-ring"></div>
                             <div className="loading-ring-inner"></div>
                             <div className="loading-ring-dot"></div>
                         </div>
 
                         {/* Titre avec effet de gradient */}
                         <h5 className="fw-bold loading-title" style={{ 
                             color: COLORS.primary,
                             background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
                             WebkitBackgroundClip: 'text',
                             WebkitTextFillColor: 'transparent',
                             backgroundClip: 'text',
                             fontSize: '1.4rem'
                         }}>
                             Chargement en cours
                         </h5>
 
                         {/* Message avec animation de points */}
                         <p className="text-muted small loading-message">
                             Récupération des données
                             <span className="loading-dots">
                                 <span>.</span><span>.</span><span>.</span>
                             </span>
                         </p>
 
                         {/* Barre de progression stylisée */}
                         <div className="loading-progress-wrapper mt-4">
                             <div className="loading-progress-track">
                                 <div className="loading-progress-bar"></div>
                             </div>
                         </div>
                     </div>
                 </div>
             </div>
             <Footer />
 
             {/* Styles intégrés pour le loading */}
             <style>{`
                 .loading-wrapper {
                     display: flex;
                     justify-content: center;
                     align-items: center;
                     min-height: 60vh;
                 }
 
                 /* Icône animée */
                 .loading-icon-wrapper {
                     position: relative;
                     width: 80px;
                     height: 80px;
                     margin: 0 auto;
                 }
                 .loading-icon-pulse {
                     animation: iconPulse 1.8s ease-in-out infinite;
                 }
                 @keyframes iconPulse {
                     0%, 100% { transform: scale(1); opacity: 1; }
                     50% { transform: scale(1.1); opacity: 0.6; }
                 }
 
                 /* Anneaux de chargement */
                 .loading-ring-wrapper {
                     position: relative;
                     width: 70px;
                     height: 70px;
                     margin: 0 auto;
                 }
                 .loading-ring {
                     position: absolute;
                     width: 100%;
                     height: 100%;
                     border-radius: 50%;
                     border: 3px solid #e9edf2;
                     border-top-color: ${COLORS.primary};
                     animation: spin 1s cubic-bezier(0.6, 0.2, 0.4, 0.8) infinite;
                 }
                 .loading-ring-inner {
                     position: absolute;
                     top: 8px;
                     left: 8px;
                     right: 8px;
                     bottom: 8px;
                     border-radius: 50%;
                     border: 2px solid #e9edf2;
                     border-bottom-color: ${COLORS.secondary};
                     animation: spin 0.7s cubic-bezier(0.4, 0.0, 0.2, 1) infinite reverse;
                 }
                 .loading-ring-dot {
                     position: absolute;
                     top: 50%;
                     left: 50%;
                     transform: translate(-50%, -50%);
                     width: 12px;
                     height: 12px;
                     border-radius: 50%;
                     background: ${COLORS.primary};
                     box-shadow: 0 0 20px ${COLORS.primary}40;
                     animation: dotPulse 1.2s ease-in-out infinite;
                 }
                 @keyframes spin {
                     to { transform: rotate(360deg); }
                 }
                 @keyframes dotPulse {
                     0%, 100% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.6; }
                     50% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
                 }
 
                 /* Titre avec dégradé (déjà géré en inline) */
                 .loading-title {
                     margin-bottom: 0.25rem;
                 }
 
                 /* Message avec points animés */
                 .loading-message {
                     font-size: 0.9rem;
                     color: #94a3b8;
                 }
                 .loading-dots span {
                     animation: dots 1.4s infinite;
                     opacity: 0;
                 }
                 .loading-dots span:nth-child(1) { animation-delay: 0s; }
                 .loading-dots span:nth-child(2) { animation-delay: 0.2s; }
                 .loading-dots span:nth-child(3) { animation-delay: 0.4s; }
                 @keyframes dots {
                     0% { opacity: 0; }
                     50% { opacity: 1; }
                     100% { opacity: 0; }
                 }
 
                 /* Barre de progression */
                 .loading-progress-wrapper {
                     max-width: 250px;
                     margin: 0 auto;
                 }
                 .loading-progress-track {
                     width: 100%;
                     height: 4px;
                     background: #e9edf2;
                     border-radius: 4px;
                     overflow: hidden;
                 }
                 .loading-progress-bar {
                     width: 0%;
                     height: 100%;
                     background: linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary});
                     background-size: 200% 100%;
                     border-radius: 4px;
                     animation: progress 2s ease-in-out infinite;
                 }
                 @keyframes progress {
                     0% { width: 0%; background-position: 200% 0; }
                     50% { width: 70%; background-position: 100% 0; }
                     100% { width: 100%; background-position: 0% 0; }
                 }
             `}</style>
         </>
     );
 }

  const patrimoines = data?.patrimoines || [];
  const pagination = data?.pagination || { total: 0, pages: 1 };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(pagination.pages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);

    if (start > 1) {
      items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
      if (start > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }
    for (let i = start; i <= end; i++) {
      items.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>{i}</Pagination.Item>);
    }
    if (end < pagination.pages) {
      if (end < pagination.pages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
      items.push(<Pagination.Item key={pagination.pages} onClick={() => handlePageChange(pagination.pages)}>{pagination.pages}</Pagination.Item>);
    }
    return items;
  };

  return (
    <>
      <style>{styles}</style>
      <Header />
      <div className="main main-app p-3 p-lg-4 dashboard-styled">
        <div className="container-fluid px-0">

          {/* ===== EN‑TÊTE MODERNE ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <i className="ri-building-2-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Patrimoines</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-bar-chart-line"></i> {stats.total} items
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.purple}15`, color: '#8b5cf6' }}>
                    <i className="ri-stack-line"></i> Quantité: {formatQuantite(stats.totalQuantite)}
                  </span>
                  <span className="badge-modern">
                    <i className="ri-calendar-line"></i> {new Date().toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              {permissions.canFilter && (
                <Button size="sm" className="btn-modern-outline" onClick={() => setShowFilters(!showFilters)}>
                  <i className="ri-filter-line"></i> {showFilters ? 'Masquer' : 'Filtres'}
                </Button>
              )}
              {permissions.canExport && (
                <>
                  <Button size="sm" className="btn-modern-success" onClick={exportToExcel}>
                    <i className="ri-file-excel-line"></i> Excel
                  </Button>
                  <Button size="sm" className="btn-modern-outline" onClick={printPDF}>
                    <i className="ri-file-pdf-line"></i> PDF
                  </Button>
                </>
              )}
              {permissions.canAdd && (
                <Button size="sm" className="btn-modern-primary" onClick={() => navigate('/patrimoinesAjouter')}>
                  <i className="ri-add-line"></i> Nouveau
                </Button>
              )}
            </div>
          </div>

          {/* ===== MESSAGE DE SUCCÈS ===== */}
          {showSuccessMessage && (
            <Alert variant="success" className="border-0 shadow-sm rounded-3 mb-3" onClose={() => setShowSuccessMessage(false)} dismissible style={{ borderRadius: '4px' }}>
              <div className="d-flex align-items-center">
                <i className="ri-checkbox-circle-line fs-4 me-2" style={{ color: COLORS.success }}></i>
                <span>{successMessage}</span>
              </div>
            </Alert>
          )}

          {/* ===== KPI CARDS ===== */}
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-primary">
                <div className="kpi-icon"><i className="ri-stack-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Total items</span>
                  <h3 className="kpi-value">{stats.total}</h3>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-purple">
                <div className="kpi-icon"><i className="ri-bar-chart-2-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Quantité totale</span>
                  <h3 className="kpi-value">{formatQuantite(stats.totalQuantite)}</h3>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-success">
                <div className="kpi-icon"><i className="ri-building-2-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Temples</span>
                  <h3 className="kpi-value">{temples.length}</h3>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-warning">
                <div className="kpi-icon"><i className="ri-calendar-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Cette année</span>
                  <h3 className="kpi-value">{new Date().getFullYear()}</h3>
                </div>
              </div>
            </Col>
          </Row>

          {/* ===== FILTRES ===== */}
          {permissions.canFilter && showFilters && (
            <Card className="filter-card mb-4 fade-in">
              <Card.Body className="p-3">
                <Form onSubmit={handleSearch}>
                  <Row className="g-2">
                    <Col md={5}>
                      <InputGroup size="sm">
                        <InputGroup.Text style={{ background: 'transparent', border: 'none', color: 'white' }}>
                          <i className="ri-search-line"></i>
                        </InputGroup.Text>
                        <Form.Control placeholder="Intitulé, description, matricule..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                      </InputGroup>
                    </Col>
                    <Col md={5}>
                      <Form.Select value={templeFilter} onChange={(e) => setTempleFilter(e.target.value)}>
                        <option value="">Tous les temples</option>
                        {temples.map(t => <option key={t.matricule} value={t.matricule}>{truncateText(t.nom, 25)}</option>)}
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Button type="submit" size="sm" className="btn-modern-primary w-100">
                        <i className="ri-filter-3-line me-1"></i> Filtrer
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          )}

          {/* ===== TABLEAU ===== */}
          <Card className="table-card">
            <div className="table-header-secondary d-flex justify-content-between align-items-center px-3 py-2">
              <span className="fw-semibold"><i className="ri-list-check me-1"></i> Liste des patrimoines</span>
              <div className="d-flex align-items-center gap-2">
                <small className="text-white-50">Lignes</small>
                <Form.Select size="sm" style={{ width: '65px', background: 'rgba(255,255,255,0.9)', border: 'none' }} value={itemsPerPage} onChange={handleItemsPerPageChange}>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </Form.Select>
              </div>
            </div>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle table-modern">
                  <thead>
                    <tr>
                      <th className="px-2">Matricule</th>
                      <th className="px-2">Intitulé</th>
                      <th className="px-2">Quantité</th>
                      <th className="px-2">Date</th>
                      <th className="px-2">Description</th>
                      <th className="px-2">Temple</th>
                      <th className="px-2 text-center" style={{ width: '70px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patrimoines.length > 0 ? (
                      patrimoines.map((patrimoine) => (
                        <tr key={patrimoine.id}>
                          <td className="px-2"><span className="reference-badge">{patrimoine.matricule}</span></td>
                          <td className="px-2">{truncateText(patrimoine.intitule, 20)}</td>
                          <td className="px-2 fw-bold">{formatQuantite(patrimoine.quantite)}</td>
                          <td className="px-2">{formatDateSimple(patrimoine.date_at)}</td>
                          <td className="px-2">{patrimoine.description ? truncateText(patrimoine.description, 15) : '-'}</td>
                          <td className="px-2">{truncateText(getTempleName(patrimoine.matri_eglise), 15)}</td>
                          <td className="px-2 text-center">
                            <div className="d-flex gap-1 justify-content-center">
                              {permissions.canEdit && (
                                <button className="action-btn action-btn-edit" title="Modifier" onClick={() => navigate(`/patrimoines/modifier/${patrimoine.id}`)}>
                                  <i className="ri-edit-line"></i>
                                </button>
                              )}
                              {permissions.canDelete && (
                                <button className="action-btn action-btn-delete" title="Supprimer" onClick={() => handleDeleteClick(patrimoine)}>
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          <i className="ri-inbox-line" style={{ fontSize: '2rem', color: '#94a3b8', display: 'block', marginBottom: '0.5rem' }}></i>
                          <p className="text-muted small">Aucun patrimoine trouvé</p>
                          {permissions.canAdd && (
                            <Button size="sm" className="btn-modern-primary" onClick={() => navigate('/patrimoinesAjouter')}>
                              <i className="ri-add-line"></i> Ajouter
                            </Button>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            {pagination.pages > 1 && (
              <Card.Footer className="border-0 bg-transparent pt-2 pb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">Page {currentPage} / {pagination.pages}</small>
                  <Pagination className="pagination-modern mb-0">
                    <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                    {renderPaginationItems()}
                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.pages} />
                    <Pagination.Last onClick={() => handlePageChange(pagination.pages)} disabled={currentPage === pagination.pages} />
                  </Pagination>
                </div>
              </Card.Footer>
            )}
          </Card>

          <Footer />
        </div>
      </div>

      {/* ===== MODAL DE SUPPRESSION ===== */}
     <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="delete-modal">
  <Modal.Header 
    closeButton 
    className="border-0 pb-0" 
    style={{ 
      background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`,
      borderTopLeftRadius: '4px',
      borderTopRightRadius: '4px'
    }}
  >
    <Modal.Title className="w-100 text-center">
      <div className="delete-icon-wrapper mb-2">
        <div className="delete-icon-circle" style={{
          width: '70px',
          height: '70px',
          borderRadius: '4px',
          background: 'rgba(255,255,255,0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto'
        }}>
          <i className="ri-delete-bin-line" style={{ fontSize: '32px', color: 'white' }}></i>
        </div>
      </div>
      <h5 className="fw-bold text-white mb-0">Confirmer la suppression</h5>
      <p className="text-white-50 small mb-0">Cette action est irréversible</p>
    </Modal.Title>
  </Modal.Header>

  <Modal.Body className="pt-4 px-4">
    {patrimoineToDelete && (
      <>
        <p className="text-center text-muted mb-3">
          Êtes-vous sûr de vouloir supprimer définitivement ce patrimoine ?
        </p>

        {/* Carte d'information du patrimoine */}
        <div className="p-3 mb-3" style={{
          background: '#f8fafc',
          borderLeft: `3px solid ${COLORS.primary}`,
          borderRadius: '4px'
        }}>
          <div className="d-flex flex-column gap-2">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted small"><i className="ri-file-text-line me-2"></i>Intitulé</span>
              <span className="fw-semibold">{patrimoineToDelete.intitule}</span>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted small"><i className="ri-barcode-line me-2"></i>Matricule</span>
              <span className="fw-semibold">{patrimoineToDelete.matricule}</span>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted small"><i className="ri-stack-line me-2"></i>Quantité</span>
              <span className="fw-semibold" style={{ color: COLORS.purple }}>{formatQuantite(patrimoineToDelete.quantite)}</span>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted small"><i className="ri-building-2-line me-2"></i>Temple</span>
              <span className="fw-semibold">{getTempleName(patrimoineToDelete.matri_eglise)}</span>
            </div>
          </div>
        </div>

        {/* Avertissement */}
        <div style={{
          background: `${COLORS.danger}10`,
          border: `1px solid ${COLORS.danger}30`,
          borderRadius: '4px',
          padding: '0.8rem 1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.8rem'
        }}>
          <i className="ri-alert-line" style={{ color: COLORS.danger, fontSize: '1.3rem' }}></i>
          <span className="small text-muted">
            Toutes les données associées à ce patrimoine seront définitivement supprimées.
          </span>
        </div>
      </>
    )}
  </Modal.Body>

  <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center gap-2">
    <Button 
      variant="light" 
      onClick={() => setShowDeleteModal(false)} 
      disabled={deleting}
      className="px-4"
      style={{
        borderRadius: '4px',
        padding: '0.5rem 1.5rem',
        border: '1px solid #d1d5db',
        color: '#4b5563',
        fontWeight: '500',
        fontSize: '0.85rem',
        transition: 'all 0.2s',
        background: 'white'
      }}
    >
      Annuler
    </Button>
    <Button
      variant="danger"
      onClick={handleDeleteConfirm}
      disabled={deleting}
      className="px-4"
      style={{
        background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`,
        border: 'none',
        borderRadius: '4px',
        padding: '0.5rem 1.5rem',
        fontWeight: '500',
        fontSize: '0.85rem',
        boxShadow: '0 4px 10px rgba(239, 68, 68, 0.25)',
        transition: 'all 0.2s',
        color: 'white'
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'translateY(-2px)';
        e.target.style.boxShadow = '0 8px 16px rgba(239, 68, 68, 0.35)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = '0 4px 10px rgba(239, 68, 68, 0.25)';
      }}
    >
      {deleting ? (
        <><Spinner size="sm" className="me-1" /> Suppression...</>
      ) : (
        <><i className="ri-delete-bin-line me-2"></i>Oui, supprimer</>
      )}
    </Button>
  </Modal.Footer>
</Modal>
    </>
  );
}